import React, { FC } from 'react';
import { TypeProduct } from '@/core/generated-types';
import { ProductAddToCart } from '../ProductAddToCart';
import { CarouselProducts } from '../Carousel';
import { ShopifyProduct } from '@/lib/shopify';
import { createImageEntry } from '@/utils/createImageEntry';
import { ProductOptionsAddToCart } from '../ProductOptionsAddToCart';
import { getVariantId, getVariantImageSet } from '@/utils/helpers';
import { useSearchParams } from 'next/navigation';

export interface ProductDetailsHeaderProps {
  shopifyProduct: ShopifyProduct | null;
  shopifyProductAddOns: { [title: string]: ShopifyProduct | null };
  shopifyAdditionalProducts: ShopifyProduct[];
  product: TypeProduct;
  additionalProducts?: TypeProduct[];
}

const ProductDetailsHeader: FC<ProductDetailsHeaderProps> = ({
  shopifyProduct,
  shopifyProductAddOns,
  shopifyAdditionalProducts,
  product,
  additionalProducts,
}) => {
  const searchParams = useSearchParams();
  const initialProductImages = product.fields.productImages || [];
  const hasVariants = shopifyProduct
    ? shopifyProduct.variants.length > 1
    : false;
  const hasProductOptions =
    shopifyProduct &&
    additionalProducts &&
    shopifyAdditionalProducts.length > 0;

  let allMedia;

  const defaultVariantId = getVariantId(shopifyProduct?.variants?.[0]);
  const variantId = searchParams?.get('variant') || defaultVariantId;
  const variantImageSet = getVariantImageSet(product, variantId);
  const hasVariantImageSets = variantImageSet?.length;

  if (hasVariantImageSets) {
    allMedia = [...variantImageSet, ...initialProductImages];
  } else {
    allMedia =
      hasVariants && shopifyProduct
        ? [
            ...initialProductImages,
            ...shopifyProduct.variants.map(createImageEntry),
          ]
        : initialProductImages;
  }

  return (
    <div className="mx-auto flex flex-col md:container md:flex-row lg:max-w-[1200px]">
      <div className="md:w-7/12">
        <CarouselProducts
          id={product?.sys.id}
          components={allMedia}
          key={hasVariantImageSets ? variantId : undefined}
        />
      </div>
      <div className="md:w-5/12">
        {hasProductOptions ? (
          <ProductOptionsAddToCart
            products={[product, ...additionalProducts]}
            shopifyProducts={[shopifyProduct, ...shopifyAdditionalProducts]}
          />
        ) : (
          product &&
          shopifyProduct && (
            <ProductAddToCart
              shopifyProduct={shopifyProduct}
              product={product}
              shopifyProductAddOns={shopifyProductAddOns}
              isVariantSwitcherDisplayed={hasVariants}
            />
          )
        )}
      </div>
    </div>
  );
};

export default React.memo(ProductDetailsHeader);
